import { useTheme } from "@mui/material";
import { CarPdfBox, CarPdfBoxProps } from "./PdfBox";
import { Circle, Line, Rect, Svg } from "@react-pdf/renderer";
import { CarPdfTypography } from "./PdfTypography";
import { CarLegendItem } from "./ChartLegend";
import { splitArrayIntoChunks } from "@app/utils";

interface PdfLegendItemProps {
  item: CarLegendItem;
  isLabelOnly?: boolean;
  style?: CarPdfBoxProps["style"];
}

const CarPdfChartLegendItem = (props: PdfLegendItemProps) => {
  const theme = useTheme();

  return (
    <CarPdfBox
      style={{
        flexDirection: "row",
        gap: 8,
        alignItems: "center",
        justifyContent: "flex-start",
        ...props.style,
      }}
    >
      {props.item.isCircle ? (
        <Svg
          width={9}
          height={9}
          viewBox={"0 0 9 9"}
          style={{ marginBottom: 1 }}
        >
          <Circle cx={4.5} cy={4.5} r={4.5} fill={theme.palette.gray6} />
          <Circle cx={4.5} cy={4.5} r={3.5} fill={props.item.color} />
        </Svg>
      ) : props.item.isDottedLine ? (
        <Svg
          width={37}
          height={3}
          viewBox={"0 0 37 3"}
          style={{ marginBottom: 1 }}
        >
          <Line
            x1="1"
            x2="37"
            y1="1.5"
            y2="1.5"
            stroke={props.item.color}
            strokeWidth="3"
            strokeDasharray="4 4"
          />
        </Svg>
      ) : (
        // to overcome bug with flexShrink 0 has no effect, this still shrinks outer box but svg stays 15px
        <CarPdfBox style={{ width: 15, flexBasis: 15 }}>
          <Svg
            width={15}
            height={15}
            viewBox={"0 0 15 15"}
            style={{
              width: 15,
              height: 15,
            }}
          >
            <Rect
              width={15}
              height={15}
              rx={4}
              ry={4}
              fill={props.item.color}
            />
          </Svg>
        </CarPdfBox>
      )}
      <CarPdfTypography
        variant="v11Semibold"
        style={{
          letterSpacing: 0.8,
        }}
      >
        {props.item.label}
      </CarPdfTypography>
    </CarPdfBox>
  );
};

interface CarPdfChartLegendProps extends CarPdfBoxProps {
  denseRows?: boolean;
  items: CarLegendItem[];
}

export const CarPdfChartLegend = ({
  items,
  denseRows,
  ...props
}: CarPdfChartLegendProps) => {
  return (
    <CarPdfBox
      {...props}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        columnGap: 20,
        rowGap: denseRows ? 8 : 20,
        ...props.style,
      }}
    >
      {items
        .filter((i) => !i.hidden)
        .map((i, idx) => (
          <CarPdfChartLegendItem key={idx} item={i} />
        ))}
    </CarPdfBox>
  );
};

export const CarPdfChartGridLegend = ({
  items,
  denseRows,
  ...props
}: CarPdfChartLegendProps) => {
  const chunks = Array.from(
    splitArrayIntoChunks(
      items.filter((i) => !i.hidden),
      3,
    ),
  );

  return (
    <CarPdfBox
      {...props}
      style={{
        flexDirection: "column",
        gap: denseRows ? 8 : 20,
        ...props.style,
      }}
    >
      {chunks.map((r, idx) => (
        <CarPdfBox
          key={idx}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 30,
          }}
          wrap={false}
        >
          {r.map((i, idx) => (
            <CarPdfChartLegendItem key={idx} item={i} style={{ width: 140 }} />
          ))}
        </CarPdfBox>
      ))}
    </CarPdfBox>
  );
};
